import NumberField from '@components/Customization/NumberField';
import { type RuleCandidate } from '@components/Customization/PoiTypes/RuleEditor';
import { FieldTypeIcon } from '@components/Customization/fieldTypes';
import {
  asBuilderCondition, BUILDER_OPERATORS, defaultCondition, valueForOperator, type SimpleVisibility,
} from '@lib/customization/poiDesign';
import { OPERATOR_VALUE_KIND, STEP_STATUS_ID } from '@lib/customization/poiRules';
import { useLocalization } from '@lib/localization';
import {
  Autocomplete, Box, Button, Chip, IconButton, ListSubheader, MenuItem, Stack, TextField, ToggleButton,
  ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import { type LocalizedText, type PoiFieldCondition, type RuleOperator } from '@store/api/poiTypeEndpoints';
import { PlusIcon, TimesIcon } from 'react-line-awesome';
import { conditionBoxStyles, segmentedStyles } from './designer.styles';
import { operatorWords } from './describe';

/** The API caps a rule at ten conditions; the button stops there. */
const MAX_CONDITIONS = 10;

/** Longer choice lists get a search box instead of a row of chips. */
const CHIP_LIMIT = 8;

type Resolve = (text?: LocalizedText) => string;

interface RowProps {
  condition: PoiFieldCondition;
  candidates: RuleCandidate[];
  resolve: Resolve;
  disabled?: boolean;
  onChange: (condition: PoiFieldCondition) => void;
  /** Absent for a rule's only condition, which cannot be removed. */
  onRemove?: () => void;
}

/** One condition as a sentence: *field* *operator* *value*. */
const ConditionRow: React.FC<RowProps> = ({ condition: stored, candidates, resolve, disabled, onChange, onRemove }) => {
  const { t } = useLocalization();

  const candidate = candidates.find((entry) => entry.id === stored.fieldId);
  const condition = asBuilderCondition(stored, candidate);
  const offered = candidate ? BUILDER_OPERATORS[candidate.type] : [];
  // An operator written elsewhere that the builder does not offer is still listed, so the
  // condition shows as it is stored rather than as an empty box.
  const operators = offered.includes(condition.operator) || !candidate ? offered : [...offered, condition.operator];
  const sources = [...new Set(candidates.map((entry) => entry.source))];
  const set = (value: PoiFieldCondition['value']) => onChange({ ...condition, value });

  const valueInput = () => {
    if (!candidate) return null;

    const kind = OPERATOR_VALUE_KIND[condition.operator];
    const options = candidate.options ?? [];

    if (kind === 'none') return null;

    if (candidate.type === 'Toggle') {
      return (
        <ToggleButtonGroup
          exclusive size="small" disabled={disabled} sx={segmentedStyles} value={condition.value === false ? 'no' : 'yes'}
          onChange={(_event, next: string | null) => next && set(next === 'yes')}
        >
          <ToggleButton value="yes">{t('Customization_Designer_Yes')}</ToggleButton>
          <ToggleButton value="no">{t('Customization_Designer_No')}</ToggleButton>
        </ToggleButtonGroup>
      );
    }

    if (kind === 'list' || (kind === 'single' && candidate.type === 'MultiList')) {
      // Only the choices the field still declares. A deleted choice's id has no chip to unselect it,
      // so it would ride along in every edit and keep the condition, and with it the save, refused.
      const selected = (Array.isArray(condition.value) ? condition.value : [])
        .filter((id) => options.some((option) => option.id === id));

      if (options.length > CHIP_LIMIT) {
        return (
          <Autocomplete
            multiple size="small" disabled={disabled} sx={{ width: '100%' }} value={selected}
            options={options.map((option) => option.id ?? '')}
            getOptionLabel={(id) => resolve(options.find((option) => option.id === id)?.label) || id}
            onChange={(_event, ids) => set(ids)}
            renderInput={(params) => <TextField {...params} placeholder={t('Customization_Designer_Select')} />}
          />
        );
      }

      return (
        <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75, width: '100%' }}>
          {options.map((option) => {
            const on = Boolean(option.id && selected.includes(option.id));

            return (
              <Chip
                key={option.id}
                size="small"
                disabled={disabled}
                label={resolve(option.label) || '…'}
                color={on ? 'primary' : 'default'}
                variant={on ? 'filled' : 'outlined'}
                onClick={() => option.id && set(on ? selected.filter((id) => id !== option.id) : [...selected, option.id])}
              />
            );
          })}
        </Stack>
      );
    }

    if (kind === 'count' || kind === 'number' || candidate.type === 'Number' || candidate.type === 'Range') {
      return (
        <NumberField
          size="small" disabled={disabled} sx={{ width: 140 }}
          allowDecimal={kind !== 'count' && candidate.decimal}
          allowNegative={kind !== 'count'}
          value={typeof condition.value === 'number' ? condition.value : undefined}
          onChange={set}
          slotProps={kind === 'count'
            ? { input: { endAdornment: <Typography variant="caption" color="text.secondary">{t('Customization_Designer_Selected')}</Typography> } }
            : undefined}
        />
      );
    }

    return (
      <TextField
        size="small" disabled={disabled} sx={{ flex: 1, minWidth: 140 }} placeholder={t('Customization_ConditionValue')}
        value={typeof condition.value === 'string' ? condition.value : ''}
        onChange={(event) => set(event.target.value)}
      />
    );
  };

  return (
    <Box sx={{ py: 0.75 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <TextField
          select
          size="small"
          disabled={disabled}
          value={candidate ? condition.fieldId : ''}
          onChange={(event) => {
            const next = candidates.find((entry) => entry.id === event.target.value);

            if (next) onChange(defaultCondition(next));
          }}
          sx={{ flex: 1, minWidth: 0 }}
          slotProps={{
            select: {
              displayEmpty: true,
              renderValue: () => (candidate
                ? (
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                    <FieldTypeIcon type={candidate.type} size={20} />
                    <Typography variant="body2" noWrap>{candidate.label}</Typography>
                  </Stack>
                )
                : <Typography variant="body2" color="warning.main">{t('Customization_Designer_PickField')}</Typography>),
            },
          }}
        >
          {/* Grouped by where each field is defined once ancestors are involved. */}
          {sources.flatMap((source) => [
            ...(sources.length > 1 ? [<ListSubheader key={`h-${source}`}>{source ?? t('Customization_Designer_Field')}</ListSubheader>] : []),
            ...candidates.filter((entry) => entry.source === source).map((entry) => (
              <MenuItem key={entry.id} value={entry.id}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <FieldTypeIcon type={entry.type} size={20} />
                  <span>{entry.groupName ? `${entry.groupName} · ${entry.label}` : entry.label}</span>
                </Stack>
              </MenuItem>
            )),
          ])}
        </TextField>
        {onRemove && (
          <Tooltip title={t('Customization_RemoveCondition')}>
            <IconButton size="small" disabled={disabled} onClick={onRemove}><TimesIcon /></IconButton>
          </Tooltip>
        )}
      </Stack>

      {candidate && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75, flexWrap: 'wrap', rowGap: 0.75 }}>
          {/* A Toggle has one sensible operator, so it reads "is" without a picker. */}
          {operators.length > 1
            ? (
              <TextField
                select size="small" disabled={disabled} sx={{ width: 170, flexShrink: 0 }}
                value={operators.includes(condition.operator) ? condition.operator : ''}
                onChange={(event) => {
                  const operator = event.target.value as RuleOperator;

                  onChange({ ...condition, operator, value: valueForOperator(condition, operator, candidate) });
                }}
              >
                {operators.map((operator) => <MenuItem key={operator} value={operator}>{operatorWords(operator, candidate.type)}</MenuItem>)}
              </TextField>
            )
            : <Typography variant="body2" color="text.secondary">{operatorWords(operators[0], candidate.type)}</Typography>}
          {valueInput()}
        </Stack>
      )}
    </Box>
  );
};

interface Props {
  visibility: SimpleVisibility;
  onChange: (visibility: SimpleVisibility) => void;
  /** Fields a condition may point at, the entry itself left out. */
  candidates: RuleCandidate[];
  resolve: Resolve;
  disabled?: boolean;
}

/**
 * "When is it asked?": always, only when, or hide when, and the conditions as sentences.
 * Writes the visibility pair the contract describes and nothing else; several rules or other
 * flags switched by answers are the rule editor's.
 */
const ConditionBuilder: React.FC<Props> = ({ visibility, onChange, candidates, resolve, disabled }) => {
  const { t } = useLocalization();

  const mode = visibility.mode === 'when' ? (visibility.show ? 'show' : 'hide') : visibility.mode;
  const conditions = visibility.mode === 'when' ? visibility.conditions : [];

  const setMode = (next: string | null) => {
    if (next === 'always' || next === 'never') {
      onChange({ mode: next });
    } else if (next === 'show' || next === 'hide') {
      onChange({
        mode: 'when',
        show: next === 'show',
        match: visibility.mode === 'when' ? visibility.match : 'all',
        conditions: conditions.length ? conditions : [defaultCondition(candidates[0])],
        ruleId: visibility.mode === 'when' ? visibility.ruleId : undefined,
      });
    }
  };

  const setConditions = (next: PoiFieldCondition[]) => visibility.mode === 'when' && onChange({ ...visibility, conditions: next });

  return (
    <Stack spacing={1.25}>
      <ToggleButtonGroup exclusive size="small" disabled={disabled} value={mode} onChange={(_event, next) => setMode(next)} sx={segmentedStyles}>
        {/* A button's own `disabled` replaces the group's, so the reader's case is repeated on these two. */}
        <ToggleButton value="always">{t('Customization_Designer_Always')}</ToggleButton>
        <ToggleButton value="show" disabled={disabled || !candidates.length}>{t('Customization_Designer_OnlyWhen')}</ToggleButton>
        <ToggleButton value="hide" disabled={disabled || !candidates.length}>{t('Customization_AddHideWhenRule')}</ToggleButton>
        {/* Only offered for a type that was stored that way: hidden with nothing to show it. */}
        {mode === 'never' && <ToggleButton value="never">{t('Customization_Designer_Never')}</ToggleButton>}
      </ToggleButtonGroup>

      {!candidates.length && <Typography variant="caption" color="text.secondary">{t('Customization_Designer_NoCandidates')}</Typography>}

      {visibility.mode === 'when' && (
        <Box sx={conditionBoxStyles}>
          {conditions.length > 1 && (
            <TextField
              select size="small" disabled={disabled} value={visibility.match} sx={{ mb: 0.5 }}
              onChange={(event) => onChange({ ...visibility, match: event.target.value as 'all' | 'any' })}
            >
              <MenuItem value="all">{t('Customization_MatchAll')}</MenuItem>
              <MenuItem value="any">{t('Customization_MatchAny')}</MenuItem>
            </TextField>
          )}

          {conditions.map((condition, index) => (
            <ConditionRow
              key={index}
              condition={condition}
              candidates={candidates}
              resolve={resolve}
              disabled={disabled}
              onChange={(next) => setConditions(conditions.map((current, position) => (position === index ? next : current)))}
              onRemove={conditions.length > 1 ? () => setConditions(conditions.filter((_, position) => position !== index)) : undefined}
            />
          ))}

          <Button
            size="small"
            startIcon={<PlusIcon />}
            disabled={disabled || conditions.length >= MAX_CONDITIONS}
            onClick={() => setConditions([...conditions, defaultCondition(candidates[0])])}
            sx={{ textTransform: 'none', mt: 0.5 }}
          >
            {t('Customization_AddCondition')}
          </Button>

          {/* The device answers this one only from the app version that knows it. */}
          {conditions.some((condition) => condition.fieldId === STEP_STATUS_ID) && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {t('Customization_StatusConditionNote')}
            </Typography>
          )}
        </Box>
      )}
    </Stack>
  );
};

export default ConditionBuilder;
